import React from 'react';
import classnames from 'classnames';
import { debounce } from 'lodash';
import styles from './rangeSlider.module.pcss';
import formatPrice from '../helpers/formatPrice';
import parsePriceNumber from '../helpers/parsePriceNumber';
import getPressedKey, { KeyboardMap } from '../helpers/getPressedKey';
import { MessagePropertiesContext } from '../contexts/messageProperties';
import { PriceSliderStyle } from '../types';

interface RangeSliderProps {
  min?: number;
  max?: number;
  currentMin?: number;
  currentMax?: number;
  minLabel?: string;
  maxLabel?: string;
  currency?: string; // if currency is set then it is a price range slider
  sliderStyle: PriceSliderStyle;
  visible?: boolean;
  isDisabled?: boolean;
  onRangeUpdate?: (min: number, max: number) => void;
}

const MIN_STEP = 5;

export const getNearstRoundBase = (currentStep: number): number => {
  if (currentStep <= 1) {
    return MIN_STEP;
  }

  let nearstRoundBase = MIN_STEP;
  const stepWidth = Math.ceil(Math.log10(currentStep));
  const stepLowLimit = 10 ** (stepWidth - 1);
  const stepHighLimit = 10 ** stepWidth;
  const stepMediumLimit = Math.round(stepHighLimit * 0.5);

  if (currentStep > stepLowLimit && currentStep <= stepMediumLimit) {
    nearstRoundBase = stepMediumLimit;
  } else if (currentStep > stepMediumLimit && currentStep <= stepHighLimit) {
    nearstRoundBase = stepHighLimit;
  }

  return nearstRoundBase;
};

export const customRound = (valueToRound: number, nearstRound: number): number =>
  Math.ceil(valueToRound / nearstRound) * nearstRound;

function RangeSlider({
  min = 0,
  max = 0,
  currentMin = 0,
  currentMax = 0,
  minLabel = '',
  maxLabel = '',
  currency = '',
  sliderStyle = 'linear',
  visible = false,
  isDisabled = false,
  onRangeUpdate = (): void => {}
}: RangeSliderProps): React.ReactElement {
  const railRef = React.useRef<HTMLDivElement>(null);
  const innerRailRef = React.useRef<HTMLSpanElement>(null);
  const rightThumbRef = React.useRef<HTMLSpanElement>(null);
  const leftThumbRef = React.useRef<HTMLSpanElement>(null);
  const [isSliding, setIsSliding] = React.useState(true);
  const [valueNowLeft, setValueNowLeft] = React.useState(Math.max(min, currentMin));
  const [valueNowRight, setValueNowRight] = React.useState(Math.min(max, currentMax));
  const [isRailMouseDown, setIsRailMouseDown] = React.useState<boolean>(false);

  let isMovingRightThumb = false;

  React.useEffect(() => {
    if (visible) {
      moveLeftThumb(currentMin, currentMax);
      moveRightThumb(currentMin, currentMax);
    }
  }, [visible]);

  React.useEffect(() => {
    if (!isSliding) {
      onRangeUpdate(valueNowLeft, valueNowRight);
    }
  }, [isSliding]);

  React.useEffect(() => {
    if (railRef.current && railRef.current.offsetWidth) {
      updateMin(currentMin, currentMax, min);
      updateMax(currentMin, currentMax, max);
    }
  }, [currentMax, max, currentMin, min]);

  React.useEffect(() => {
    const debouncedHandleResize = debounce(() => {
      moveLeftThumb(currentMin, currentMax);
      moveRightThumb(currentMin, currentMax);
    }, 1200);

    window.addEventListener('resize', debouncedHandleResize);

    return (): void => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  });

  const [formattedMessage] = React.useContext(MessagePropertiesContext);

  // arbitrary keydown slide steps (TODO: define steps behavior with business)
  const shortStep = Math.round((max - min) / 100);
  const longStep = Math.round((max - min) / 10);

  function isRightThumb(event): boolean {
    const target = event.target as HTMLSpanElement;
    return target.className.includes(styles['range-slider__thumb--right']);
  }

  function handleKeyUp(): void {
    setIsSliding(false);
  }

  function handleKeyDown(event: React.KeyboardEvent): void {
    const pressedKey = getPressedKey(event);
    let validKey = true;
    let targetValue = 0;
    const valueNow = isRightThumb(event) ? valueNowRight : valueNowLeft;
    setIsSliding(true);

    switch (pressedKey) {
      case KeyboardMap.ARROW_LEFT:
      case KeyboardMap.ARROW_DOWN:
        targetValue = valueNow - shortStep;
        break;

      case KeyboardMap.ARROW_RIGHT:
      case KeyboardMap.ARROW_UP:
        targetValue = valueNow + shortStep;
        break;

      case KeyboardMap.PAGE_DOWN:
        targetValue = valueNow - longStep;
        break;

      case KeyboardMap.PAGE_UP:
        targetValue = valueNow + longStep;
        break;

      case KeyboardMap.HOME:
        targetValue = min;
        break;

      case KeyboardMap.END:
        targetValue = max;
        break;

      default:
        validKey = false;
        break;
    }

    if (validKey) {
      // prevent page scroll
      stopEvent(event);

      if (isRightThumb(event)) {
        updateMax(currentMin, targetValue, max);
      } else {
        updateMin(targetValue, currentMax, min);
      }
    }
  }

  function stopEvent(event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  function handleMouseMove(event: MouseEvent): void {
    stopEvent(event);
    handleMove(event.pageX);
  }

  function handleTouchMove(event: TouchEvent): void {
    handleMove(event.changedTouches[0].pageX);
  }

  interface StepData {
    nextStep: number;
    diffX: number;
    stepAwareNearstRoundBase: number;
    scale?: number;
  }

  function getNextStepData(offsetX: number): StepData {
    if (railRef.current) {
      const railWidth = railRef.current.offsetWidth;
      const railOffsetLeft = railRef.current.getBoundingClientRect().left;
      const diffX = offsetX - railOffsetLeft;

      const resultObj = {
        railWidth,
        diffX
      };

      if (sliderStyle === 'linear') {
        const priceRange = max - min;
        const nextStep = priceRange / railWidth || 1;
        const stepAwareNearstRoundBase = getNearstRoundBase(nextStep);
        return {
          ...resultObj,
          nextStep,
          stepAwareNearstRoundBase
        };
      }

      if (sliderStyle === 'logarithmic') {
        const scale = (Math.log(max) - Math.log(min)) / railWidth;
        const nextStep = diffX * scale;
        const stepAwareNearstRoundBase = getNearstRoundBase(Math.exp(nextStep));

        return {
          ...resultObj,
          nextStep,
          scale,
          stepAwareNearstRoundBase
        };
      }
    }

    return {
      nextStep: 0,
      diffX: 0,
      stepAwareNearstRoundBase: 1
    };
  }

  function getTargetValue(offsetX: number): number {
    if (railRef.current) {
      const { diffX, nextStep, stepAwareNearstRoundBase } = getNextStepData(offsetX);

      if (sliderStyle === 'linear') {
        const linearTargetValue = min + nextStep * diffX;

        return customRound(linearTargetValue, stepAwareNearstRoundBase);
      }
      if (sliderStyle === 'logarithmic') {
        const logStepAwareNearstRoundBase =
          min < MIN_STEP ? Math.floor(stepAwareNearstRoundBase / MIN_STEP) : stepAwareNearstRoundBase;

        return customRound(Math.floor(Math.exp(nextStep + Math.log(min))), logStepAwareNearstRoundBase);
      }
      return 0;
    }
    return 0;
  }

  function handleMove(offsetX: number): void {
    const { stepAwareNearstRoundBase } = getNextStepData(offsetX);

    if (isMovingRightThumb) {
      const maxLeftOffset: number = leftThumbRef?.current?.getBoundingClientRect().right || 0;

      if (offsetX > maxLeftOffset + 6) {
        updateMax(currentMin, getTargetValue(offsetX), max, stepAwareNearstRoundBase);
      }
    } else {
      const maxRightOffset: number = rightThumbRef?.current?.getBoundingClientRect().left || 0;

      if (offsetX < maxRightOffset - 6) {
        updateMin(getTargetValue(offsetX), currentMax, min, stepAwareNearstRoundBase);
      }
    }
  }

  function stopListening(): void {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('mouseup', handleTouchEnd);
    document.removeEventListener('touchend', handleTouchEnd);
  }

  function handleTouchEnd(): void {
    stopListening();
    setIsSliding(false);
  }

  function handleTouchStart(event: React.TouchEvent<HTMLSpanElement>): void {
    isMovingRightThumb = isRightThumb(event);
    setIsSliding(true);

    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);
  }

  function handleMouseDown(event: React.MouseEvent<HTMLSpanElement>): void {
    // Only handle left clicks
    if (event.button !== 0) {
      return;
    }

    isMovingRightThumb = isRightThumb(event);
    setIsSliding(true);

    stopEvent(event);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleTouchEnd);
  }

  function getThumbPosition(value: number): number {
    const railWidth = railRef.current ? railRef.current.offsetWidth : 1;
    if (sliderStyle === 'linear') {
      return Math.round(((value - min) * railWidth) / (max - min));
    }
    if (sliderStyle === 'logarithmic') {
      const scale = (Math.log(max) - Math.log(min)) / railWidth;
      return Math.round((Math.log(value) - Math.log(min)) / scale);
    }
    return 0;
  }

  function restrictValueToRange(value: number, maxValue: number, minValue: number): number {
    if (value > maxValue) {
      value = maxValue;
    }

    if (value < minValue) {
      value = minValue;
    }

    return value;
  }

  function updateRailProperties(width: number, left?: number): void {
    if (innerRailRef.current && leftThumbRef.current) {
      innerRailRef.current.style.width = `${width}px`;

      if (left !== undefined) {
        innerRailRef.current.style.left = `${left}px`;
      }
    }
  }

  function moveLeftThumb(value: number, currentMaxValue: number): void {
    if (leftThumbRef.current) {
      const thumbPosition = getThumbPosition(value);
      const thumbOffset = -leftThumbRef.current.offsetWidth / 2;

      leftThumbRef.current.style.left = `${thumbPosition + thumbOffset}px`;
      updateRailProperties(getThumbPosition(currentMaxValue) - thumbPosition, thumbPosition);
    }
  }

  function moveRightThumb(currentMinValue: number, value: number): void {
    if (rightThumbRef.current) {
      const thumbPosition = getThumbPosition(value);
      const thumbOffset = -rightThumbRef.current.offsetWidth / 2;

      rightThumbRef.current.style.left = `${thumbPosition + thumbOffset}px`;
      updateRailProperties(thumbPosition - getThumbPosition(currentMinValue));
    }
  }

  function updateMax(currentMinValue: number, value: number, maxValue: number, nextStep?: number): void {
    value = value ? restrictValueToRange(value, maxValue, currentMinValue) : max;
    const nextIncrement = nextStep || 1;
    if (value === currentMinValue) {
      value += nextIncrement;
    }
    setValueNowRight(value);
    moveRightThumb(currentMinValue, value);
  }

  function updateMin(value: number, currentMaxValue: number, minValue: number, nextStep?: number): void {
    value = restrictValueToRange(value, currentMaxValue, minValue);
    const nextIncrement = nextStep || 1;
    if (value === currentMaxValue) {
      value -= nextIncrement;
    }
    setValueNowLeft(value);
    moveLeftThumb(value, currentMaxValue);
  }

  function getEventValue(value: string): number {
    return currency ? parsePriceNumber(value) : parseFloat(value);
  }

  function leftValueChanged(event: React.ChangeEvent<HTMLInputElement>): void {
    setIsSliding(true);
    setValueNowLeft(getEventValue(event.target.value));
  }

  function rightValueChanged(event: React.ChangeEvent<HTMLInputElement>): void {
    setIsSliding(true);
    setValueNowRight(getEventValue(event.target.value));
  }

  function clickedOnRail(event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>): boolean {
    const target = event.target as HTMLDivElement;
    return target.className.includes('rail');
  }

  function handleRailMouseDown(): void {
    setIsSliding(true);
    setIsRailMouseDown(true);
  }

  function updateSlider(pageX: number): void {
    if (leftThumbRef.current && rightThumbRef.current) {
      isMovingRightThumb =
        Math.abs(pageX - rightThumbRef.current.getBoundingClientRect().left) <
        Math.abs(pageX - leftThumbRef.current.getBoundingClientRect().left);

      setIsSliding(false);
      handleMove(pageX);
    }
  }

  function handleRailMouseUp(event: React.MouseEvent<HTMLDivElement>): void {
    if (clickedOnRail(event) && isRailMouseDown) {
      setIsRailMouseDown(false);
      updateSlider(event.pageX);
    }
  }

  function handleRailTouchEnd(event: React.TouchEvent<HTMLDivElement>): void {
    if (clickedOnRail(event)) {
      updateSlider(event.changedTouches[0].pageX);
    }
  }

  return (
    <div className={styles['range-slider']}>
      <div className={styles['range-slider__labels']}>
        <div className={classnames(styles['range-slider__label'], isSliding && styles['range-slider__label--active'])}>
          <input
            type="text"
            name="rangeFrom"
            disabled={isDisabled}
            onChange={leftValueChanged}
            onBlur={(): void => {
              setIsSliding(false);
              updateMin(valueNowLeft, valueNowRight, min);
            }}
            value={currency ? formatPrice(valueNowLeft, currency) : valueNowLeft}
          />
        </div>
        <p className={styles['range-slider__label--between']}>
          {formattedMessage({ id: 'search.nav.facetRangeTo', fallback: 'to' })}
        </p>
        <div className={classnames(styles['range-slider__label'], isSliding && styles['range-slider__label--active'])}>
          <input
            type="text"
            name="rangeTo"
            disabled={isDisabled}
            onChange={rightValueChanged}
            onBlur={(): void => {
              setIsSliding(false);
              updateMax(valueNowLeft, valueNowRight, max);
            }}
            value={currency ? formatPrice(valueNowRight, currency) : valueNowRight}
          />
        </div>
      </div>
      <div
        className={classnames(styles['range-slider__rail'], isDisabled && styles['range-slider__rail--disabled'])}
        tabIndex={0}
        role="button"
        ref={railRef}
        onMouseDown={handleRailMouseDown}
        onMouseUp={handleRailMouseUp}
        onTouchStart={handleRailMouseDown}
        onTouchEnd={handleRailTouchEnd}
      >
        <span className={styles['range-slider__rail-inner']} ref={innerRailRef} />
        <span
          role="slider"
          tabIndex={0}
          className={`${styles['range-slider__thumb']} ${styles['range-slider__thumb--left']}`}
          aria-valuemin={min}
          aria-valuenow={valueNowLeft}
          aria-valuetext={currency ? formatPrice(valueNowLeft, currency) : valueNowLeft.toString()}
          aria-valuemax={valueNowRight}
          aria-label={minLabel}
          ref={leftThumbRef}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        />

        <span
          role="slider"
          tabIndex={0}
          className={`${styles['range-slider__thumb']} ${styles['range-slider__thumb--right']}`}
          aria-valuemin={valueNowLeft}
          aria-valuenow={valueNowRight}
          aria-valuetext={currency ? formatPrice(valueNowRight, currency) : valueNowRight.toString()}
          aria-valuemax={max}
          aria-label={maxLabel}
          ref={rightThumbRef}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        />
      </div>
    </div>
  );
}

export default RangeSlider;
